/**
 * The ONE transcript model + tool filter shared by every AI chat surface (the
 * agent builder chats and btgpt), live and saved. Live stream chunks and saved
 * transcripts both go through `isHiddenTool` / `toolDisplayName`, so the two
 * views can never disagree about which tools are shown or what they are called.
 *
 * A tool is described by its NAME only — never its arguments or result
 * (rev33 QA contract: no internal text reaches the UI).
 */

/**
 * #813 step 4 — the builder stopped to ask whether to change an EXISTING agent or make a new one.
 * Rendered as a confirm card. Mirrors the backend's `BuilderAsk` (agent id + label only, never content).
 */
export type BuilderAsk =
  | { reason: 'unnamed_existing_agent'; agentId: string; label: string }
  | { reason: 'duplicate_name'; agentId: string; label: string };

/** Narrow wire data to a {@link BuilderAsk}; anything else → null (the row then reads as plain text). */
export function parseBuilderAsk(value: unknown): BuilderAsk | null {
  if (typeof value !== 'object' || value === null) return null;
  if (!('reason' in value) || !('agentId' in value) || !('label' in value)) return null;
  const { reason, agentId, label } = value;
  if (typeof agentId !== 'string' || agentId === '' || typeof label !== 'string') return null;
  if (reason === 'unnamed_existing_agent') return { reason: 'unnamed_existing_agent', agentId, label };
  if (reason === 'duplicate_name') return { reason: 'duplicate_name', agentId, label };
  return null;
}

/** One row of a conversation, as `AiChat` renders it. `id` is stable per row (React key + `renderExtra` lookup). */
export type AiChatItem =
  | { id: string; kind: 'user'; text: string | null; attachments?: Array<{ name: string; mime: string }> }
  | { id: string; kind: 'assistant'; text: string; error?: boolean; ask?: BuilderAsk }
  | { id: string; kind: 'tool'; name: string };

/**
 * The backend's refusal of a builder turn with no AI credits left (`BUILDER_NO_CREDITS_TEXT`,
 * an English string with no error code on the wire yet) — the chat adds a Buy credits button to it.
 */
export function isNoCreditsNotice(text: string): boolean {
  return /^You're out of AI credits\b/.test(text.trim());
}

/** Where Buy credits goes: Credits & Usage, with the add-credits dialog already open. */
export const BUY_CREDITS_PATH = '/credits-usage?openAddCredits=true';

/** Internal plumbing the user never needs to see (tool search, deferred-tool loading). */
export const HIDDEN_TOOLS: ReadonlySet<string> = new Set(['ToolSearch']);

/** `mcp__blueticks__scheduled_messages` → `scheduled_messages`. */
export function stripMcpPrefix(raw: string): string {
  return raw.replace(/^mcp__[^_]+(?:_[^_]+)*?__/, '');
}

/** True when a raw tool name must not be shown (live or saved). */
export function isHiddenTool(raw: string | undefined | null): boolean {
  if (!raw) return true;
  return HIDDEN_TOOLS.has(raw) || HIDDEN_TOOLS.has(stripMcpPrefix(raw));
}

/**
 * A readable name for a raw tool name — from the NAME only.
 * `mcp__blueticks__scheduled_messages` → `scheduled messages`; `WebSearch` → `web search`.
 * Never returns anything containing `mcp__`.
 */
export function toolDisplayName(raw: string): string {
  const name = stripMcpPrefix(raw)
    .replace(/mcp__/g, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return name || 'tool';
}

/**
 * Consecutive calls to the same tool, folded into one step with a count
 * ("Checked chats ×2"). The true number of calls is still `names.length`.
 */
export function groupToolRuns(names: string[]): { name: string; count: number }[] {
  const runs: { name: string; count: number }[] = [];
  for (const name of names) {
    const last = runs[runs.length - 1];
    if (last && toolDisplayName(last.name) === toolDisplayName(name)) last.count++;
    else runs.push({ name, count: 1 });
  }
  return runs;
}

/* ── Agent builder transcript → items ──────────────────────────────────────── */

/** The builder transcript turn shape (structurally `MyBridgeTranscriptTurn`). */
export interface BuilderTranscriptTurn {
  role: 'user' | 'assistant' | 'system' | 'tool' | 'thinking';
  text: string;
  /** Old pod transcripts: a `role: 'tool'` turn names its tool here. */
  toolName?: string;
  /** Assistant turns (release 11): the tools the turn called, raw names in call order; absent when none. */
  toolCalls?: { tool: string }[];
  /** User turns (Task 7): the files attached to this turn — name + mime only, never a URL/id. */
  attachments?: { name: string; mime: string }[];
  /** Assistant turns (#813 step 4): a confirm card — update the existing agent / create a new one. */
  ask?: BuilderAsk;
}

/**
 * The builder turns carry an instructions block; the user sees only what they
 * typed. New turns lead with `[USER MESSAGE]…[/USER MESSAGE]`; older ones put the
 * words at the END after the framing, which the transcript may have clipped off
 * (null → shown as a placeholder).
 */
const OPEN = '[USER MESSAGE]\n';
const CLOSE = '\n[/USER MESSAGE]';
const OLD_MARKERS = ["The user's message:\n\n", "The user's request:\n\n"];
export function userText(raw: string): string | null {
  if (raw.startsWith(OPEN)) {
    const end = raw.indexOf(CLOSE, OPEN.length);
    return (end >= 0 ? raw.slice(OPEN.length, end) : raw.slice(OPEN.length)).trim();
  }
  for (const m of OLD_MARKERS) {
    const i = raw.lastIndexOf(m);
    if (i >= 0) return raw.slice(i + m.length).trim();
  }
  if (/^\[AGENT (BUILDER|EDIT) MODE\]/.test(raw)) return null;
  return raw.trim();
}

/** Builder transcript → chat items (user words only, visible tools by name, no thinking/system). */
export function toItems(turns: BuilderTranscriptTurn[]): AiChatItem[] {
  const out: AiChatItem[] = [];
  turns.forEach((tn, i) => {
    const id = `t${i}`;
    if (tn.role === 'user') {
      const t = userText(tn.text ?? '');
      if (t !== '') out.push({ id, kind: 'user', text: t, ...(tn.attachments?.length ? { attachments: tn.attachments } : {}) });
    } else if (tn.role === 'assistant') {
      // Its tools first (the same order the live stream showed them), even when the text is empty.
      (tn.toolCalls ?? []).forEach((call, k) => {
        if (!isHiddenTool(call.tool)) out.push({ id: `${id}.${k}`, kind: 'tool', name: call.tool });
      });
      if ((tn.text ?? '').trim()) {
        const ask = parseBuilderAsk(tn.ask);
        out.push({ id, kind: 'assistant', text: tn.text.trim(), ...(ask ? { ask } : {}) });
      }
    } else if (tn.role === 'tool' && tn.toolName && !isHiddenTool(tn.toolName)) {
      out.push({ id, kind: 'tool', name: tn.toolName });
    }
  });
  return out;
}
